import { createFileRoute, Link, useBlocker, useNavigate } from '@tanstack/react-router'
import type { GuidedMissing } from '@gymcloud/nutrition'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useAuth } from '~/auth/auth-context'
import { Working } from '~/components/working'
import { reached, removed, useWork } from '~/components/use-work'
import { ConfirmDialog } from '~/components/confirm-dialog'
import { DialogLink } from '~/components/dialog-link'
import { Choice } from '~/components/food-form-dialog'
import { ChevronLeftIcon, DeleteIcon } from '~/components/icons'
import { Skeleton, SkeletonBlock } from '~/components/skeleton'
import { useToast } from '~/components/toast-context'
import { UnitInput } from '~/components/unit-input'
import { todayKey } from '~/data/weighins'
import { useWeighIns } from '~/data/weighins-store'
import { useApi } from '~/data/data-context'
import { coachedTargets, expenditureOf } from '~/data/expenditure'
import {
  CALORIE_FLOOR_CHOICES,
  CALORIE_FLOOR_LABELS,
  CALORIE_LEVELS,
  CALORIE_LEVEL_LABELS,
  DEFAULT_PROGRAM,
  DIETS,
  DIET_LABELS,
  FLOOR_CALORIES,
  GOAL_STEP_TITLES,
  MAINTAIN_WINDOW_KG,
  NUTRIENT_LABELS,
  NUTRIENT_UNITS,
  PROTEIN_INTAKES,
  PROTEIN_INTAKE_LABELS,
  RATE_CEILING_SHARE,
  RATE_WARNING_SHARE,
  TARGETED,
  WEEKS_PER_MONTH,
  currentGoal,
  currentGoalOn,
  distanceSaid,
  evenDistribution,
  expectedOnAtRate,
  formatCalories,
  goalSteps,
  isEvenWeek,
  progressToward,
  type Aim,
  type DayTargets,
  type GoalMode,
  type GoalStep,
  type GuidedTargets,
  type Program,
} from '~/data/nutrition'
import { dropGoal, keepGoal, reloadNutrition, useNutrition } from '~/data/nutrition-store'
import { dayStart, formatDayLabel } from '~/lib/dates'
import { withoutDialog } from '~/lib/dialogs'
import { trendOf } from '~/lib/metric-series'
import { displayToKg, kgToDisplay } from '~/lib/units'
import { weekOrder } from '~/lib/weekdays'

export const Route = createFileRoute('/you_/goal')({
  component: GoalPage,
})

const AIMS: { aim: Aim; label: string }[] = [
  { aim: 'lose', label: 'Lose' },
  { aim: 'maintain', label: 'Maintain' },
  { aim: 'gain', label: 'Gain' },
]

/** The seven weekdays a program is written over, Monday first, as the goal keeps them. */
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7]

/**
 * What the coached way has to say: the targets, or what the account is missing for there to
 * be any — the stats, a weight, or simply an estimate not yet made.
 */
type Coached =
  | { readonly state: 'got'; readonly targets: GuidedTargets }
  | { readonly state: 'missing'; readonly missing: readonly GuidedMissing[] }

/** Four targets as they are typed, before any of them is a number. */
type TargetText = Record<(typeof TARGETED)[number], string>

const EMPTY_TARGETS: TargetText = { calories: '', protein: '', carbs: '', fat: '' }

function targetsOf(text: TargetText): DayTargets {
  return {
    calories: Number(text.calories),
    protein: Number(text.protein),
    carbs: Number(text.carbs),
    fat: Number(text.fat),
  }
}

function targetsFilled(text: TargetText): boolean {
  const read = targetsOf(text)
  return TARGETED.every(
    (name) => text[name].trim() !== '' && Number.isFinite(read[name]) && read[name] >= 0,
  )
}

/** A number as a field shows one: to a place or two, without a tail of zeroes. */
function said(value: number, places = 2): string {
  return String(Math.round(value * 10 ** places) / 10 ** places)
}

function GoalPage() {
  const data = useNutrition()
  const weighIns = useWeighIns()
  if (data === null || weighIns === null) {
    return (
      <SkeletonBlock label="Loading goal" className="mx-auto max-w-3xl space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </SkeletonBlock>
    )
  }
  return <GoalProcess key={currentGoal(data.goals)?.id ?? 'none'} data={data} weighIns={weighIns} />
}

/**
 * Setting a goal, per the spec: a process of steps, one question a step. Coached — the aim,
 * then a target and a rate, then the program one setting at a time — or by hand, the targets
 * typed for the whole week or for each day of it. Both ways end on the same review, which
 * names every answer and is where the goal is finally set.
 */
function GoalProcess({
  data,
  weighIns,
}: {
  data: NonNullable<ReturnType<typeof useNutrition>>
  weighIns: NonNullable<ReturnType<typeof useWeighIns>>
}) {
  const api = useApi()
  const toast = useToast()
  const navigate = useNavigate()
  const { units, settings } = useAuth()
  // The goal being changed, and what it is asking of a day right now: the wizard opens on
  // the numbers in force, which are the newest of the goal's run rather than its first.
  const goal = currentGoalOn(data.goals, todayKey())
  const today = todayKey()
  /*
   * The expenditure, worked out here from the diary, the weigh-ins and the stats — the
   * nutrition spec, Expenditure. Everything the coached way says follows from it.
   */
  const spent = useMemo(
    () => expenditureOf(data, weighIns, settings, today),
    [data, weighIns, settings, today],
  )
  const weightKg = spent.latestWeightKg

  // Sent here to be coached — by the import, whose goals arrive set by hand — the process
  // opens on that way whatever the goal in force was set by.
  const { way, aim: sentAim } = Route.useSearch()
  const [mode, setMode] = useState<GoalMode>(
    way === 'coached' ? 'guided' : (goal?.mode ?? 'guided'),
  )
  /*
   * Sent here aiming a particular way — reaching a goal and choosing the other one — the
   * process opens on that aim rather than on the aim of the goal just reached, which is the
   * one being left behind.
   */
  const [aim, setAim] = useState<Aim>(sentAim ?? goal?.aim ?? 'lose')
  const [targetWeight, setTargetWeight] = useState(
    goal?.targetWeightKg == null ? '' : said(kgToDisplay(goal.targetWeightKg, units), 1),
  )
  /** The rate as the slider holds it: a share of the body weight a week, per cent. */
  const [ratePercent, setRatePercent] = useState(
    goal?.rateKgPerWeek == null || weightKg === null || weightKg <= 0
      ? 0.5
      : Math.round((goal.rateKgPerWeek / weightKg) * 1000) / 10,
  )
  /** Which of the four ways the rate is written is being typed into, and what stands there. */
  const [ratePut, setRatePut] = useState<{ field: string; text: string } | null>(null)
  const [program, setProgram] = useState<Program>({
    diet: goal?.diet ?? DEFAULT_PROGRAM.diet,
    calorieFloor: goal?.calorieFloor ?? DEFAULT_PROGRAM.calorieFloor,
    proteinIntake: goal?.proteinIntake ?? DEFAULT_PROGRAM.proteinIntake,
    distribution: goal?.calorieDistribution ?? evenDistribution(),
  })
  // Monday's until changed in settings; a goal changed keeps the weekday it had.
  const weeklyCheckinWeekday = goal?.weeklyCheckinWeekday ?? 1
  const [sameAllWeek, setSameAllWeek] = useState(goal?.dayTargets == null)
  const [byHand, setByHand] = useState<TargetText>(
    goal?.mode !== 'manual'
      ? EMPTY_TARGETS
      : {
          calories: String(goal.calories),
          protein: String(goal.protein),
          carbs: String(goal.carbs),
          fat: String(goal.fat),
        },
  )
  const [byHandDays, setByHandDays] = useState<TargetText[]>(() =>
    goal?.dayTargets == null
      ? WEEKDAYS.map(() => EMPTY_TARGETS)
      : goal.dayTargets.map((day) => ({
          calories: String(day.calories),
          protein: String(day.protein),
          carbs: String(day.carbs),
          fat: String(day.fat),
        })),
  )
  const [at, setAt] = useState(0)
  /** Set where a step was reached from the review, which is where answering it goes back to. */
  const [backToReview, setBackToReview] = useState(false)
  const saving = useWork()
  const [removing, setRemoving] = useState(false)

  const steps = goalSteps({ mode, aim, sameAllWeek })
  const step: GoalStep = steps[Math.min(at, steps.length - 1)] ?? 'way'

  const targetWeightKg =
    targetWeight.trim() === '' || !Number.isFinite(Number(targetWeight))
      ? null
      : displayToKg(Number(targetWeight), units)
  const rateKg = aim === 'maintain' ? null : (ratePercent / 100) * (weightKg ?? 0) || null

  /*
   * The coached targets follow from the expenditure and the answers so far — the aim, the
   * rate, the program, and a maintaining goal's target — in the same frame they change, with
   * nothing asked of the server. The nutrition spec, The goal itself.
   */
  const asked: Coached =
    mode === 'guided'
      ? (() => {
          const got = coachedTargets(
            spent,
            settings,
            aim,
            rateKg,
            program,
            aim === 'maintain' ? targetWeightKg : null,
          )
          return 'targets' in got
            ? { state: 'got', targets: got.targets }
            : { state: 'missing', missing: got.missing }
        })()
      : { state: 'missing', missing: [] }
  const coached = asked.state === 'got' ? asked.targets : null
  const expenditure = coached?.expenditure ?? spent.value
  const statsMissing = asked.state === 'missing' && asked.missing.includes('stats')
  const weightMissing = asked.state === 'missing' && asked.missing.includes('weight')

  const trend = trendOf(
    [...weighIns]
      .filter((weighIn) => weighIn.weightKg !== null)
      .sort((a, b) => (a.day < b.day ? -1 : 1))
      .map((weighIn) => ({ day: weighIn.day, value: weighIn.weightKg ?? 0 })),
  )
  const trendKg = trend[trend.length - 1]?.value ?? weightKg

  const wrongSide =
    targetWeightKg !== null &&
    weightKg !== null &&
    ((aim === 'lose' && targetWeightKg >= weightKg) ||
      (aim === 'gain' && targetWeightKg <= weightKg))
  const outsideWindow =
    aim === 'maintain' &&
    targetWeightKg !== null &&
    trendKg !== null &&
    Math.abs(targetWeightKg - trendKg) > MAINTAIN_WINDOW_KG
  const rateHigh = rateKg !== null && weightKg !== null && rateKg > weightKg * RATE_WARNING_SHARE

  /** What the goal would be set to: the coached targets, or what was typed by hand. */
  const dayTargets: DayTargets[] | null =
    mode === 'guided'
      ? (coached?.days.map((day) => ({ ...day })) ?? null)
      : sameAllWeek
        ? null
        : byHandDays.map((day) => targetsOf(day))
  const week: DayTargets | null =
    mode === 'guided'
      ? coached
      : sameAllWeek
        ? targetsFilled(byHand)
          ? targetsOf(byHand)
          : null
        : byHandDays.every(targetsFilled)
          ? {
              calories: Math.round(
                byHandDays.reduce((sum, day) => sum + Number(day.calories), 0) / 7,
              ),
              protein: Math.round(
                byHandDays.reduce((sum, day) => sum + Number(day.protein), 0) / 7,
              ),
              carbs: Math.round(byHandDays.reduce((sum, day) => sum + Number(day.carbs), 0) / 7),
              fat: Math.round(byHandDays.reduce((sum, day) => sum + Number(day.fat), 0) / 7),
            }
          : null

  /*
   * Why the step standing cannot be gone on from, or null where it can. A reason the step
   * already says in its own words is marked unsaid, so it is not printed twice.
   */
  const stop: { why: string; said: boolean } | null = (() => {
    switch (step) {
      case 'rate':
        if (statsMissing || weightMissing) {
          return { why: 'The coached way needs your body stats and a body weight.', said: false }
        }
        if (targetWeightKg === null) {
          return { why: 'Fill in a target weight.', said: true }
        }
        if (wrongSide) {
          return { why: 'The target weight is on the wrong side of your weight.', said: false }
        }
        return rateKg === null || rateKg <= 0
          ? { why: 'Choose a rate above nothing.', said: true }
          : null
      case 'maintain':
        if (statsMissing || weightMissing) {
          return { why: 'The coached way needs your body stats and a body weight.', said: false }
        }
        if (targetWeightKg === null) {
          return { why: 'Fill in a target trend weight.', said: true }
        }
        return outsideWindow
          ? { why: 'The target is too far from the trend weight.', said: false }
          : null
      case 'manual-targets':
        return week === null ? { why: 'Fill in the calories and each macro.', said: true } : null
      case 'review':
        return week === null ? { why: 'The targets are not settled yet.', said: true } : null
      default:
        return null
    }
  })()
  const blocker = stop === null ? null : stop.why

  const answered =
    mode !== (goal?.mode ?? 'guided') ||
    at > 0 ||
    targetWeight !== '' ||
    TARGETED.some((name) => byHand[name] !== '')
  const holding = useRef(false)
  useEffect(() => {
    holding.current = answered && !saving.working
  }, [answered, saving.working])
  const leaving = useBlocker({
    shouldBlockFn: () => holding.current,
    enableBeforeUnload: false,
    withResolver: true,
  })

  const goTo = (which: GoalStep) => {
    const found = steps.indexOf(which)
    if (found >= 0) {
      setAt(found)
    }
  }
  const onward = () => {
    if (blocker !== null) {
      return
    }
    if (backToReview) {
      setBackToReview(false)
      goTo('review')
      return
    }
    setAt((held) => Math.min(held + 1, steps.length - 1))
  }
  const backward = () => {
    setBackToReview(false)
    setAt((held) => Math.max(0, held - 1))
  }
  const change = (which: GoalStep) => {
    setBackToReview(true)
    goTo(which)
  }

  const save = () =>
    saving.run(async () => {
      if (week === null) {
        return
      }
      holding.current = false
      try {
        const saved = await api.putGoal({
          effectiveFrom: today,
          mode,
          aim: mode === 'guided' ? aim : null,
          targetWeightKg: mode === 'guided' ? targetWeightKg : null,
          rateKgPerWeek: mode === 'guided' ? rateKg : null,
          calories: week.calories,
          protein: week.protein,
          carbs: week.carbs,
          fat: week.fat,
          weeklyCheckinWeekday,
          expenditureAtSet: mode === 'guided' ? expenditure : null,
          ...(mode === 'guided'
            ? {
                diet: program.diet,
                calorieFloor: program.calorieFloor,
                proteinIntake: program.proteinIntake,
                calorieDistribution: [...program.distribution],
              }
            : {}),
          dayTargets: dayTargets === null || isEvenWeek(dayTargets) ? null : dayTargets,
        })
        keepGoal(saved)
        reloadNutrition()
        toast('Goal set from today.')
        void navigate({ to: '/diary', search: withoutDialog })
      } catch {
        holding.current = true
        toast('That did not reach us. Try again in a moment.')
      }
    })

  /*
   * Removing the goal leaves for the diary, which says what was done — the page was about a
   * goal, and there is no longer one to be about.
   */
  const remove = async () => {
    // Asked from its confirmation, which says in itself where the server was not reached.
    await reached(() => removed(() => api.deleteGoal()))
    dropGoal()
    holding.current = false
    reloadNutrition()
    toast('Goal removed.')
    void navigate({ to: '/diary', search: withoutDialog })
  }

  const progress =
    goal?.targetWeightKg == null ? null : progressToward(trend, goal.targetWeightKg, today)
  const expectedDay = expectedOnAtRate(weightKg, targetWeightKg, rateKg, today)

  /** The rate written four ways, and what typing into any of them means. */
  const rateFields: {
    key: string
    label: string
    value: number
    toPercent: (n: number) => number
  }[] = [
    {
      key: 'week-share',
      label: 'Per week %',
      value: ratePercent,
      toPercent: (n) => n,
    },
    {
      key: 'week-weight',
      label: `Per week ${units}`,
      value: kgToDisplay((ratePercent / 100) * (weightKg ?? 0), units),
      toPercent: (n) =>
        weightKg === null || weightKg <= 0 ? 0 : (displayToKg(n, units) / weightKg) * 100,
    },
    {
      key: 'month-share',
      label: 'Per month %',
      value: ratePercent * WEEKS_PER_MONTH,
      toPercent: (n) => n / WEEKS_PER_MONTH,
    },
    {
      key: 'month-weight',
      label: `Per month ${units}`,
      value: kgToDisplay((ratePercent / 100) * (weightKg ?? 0) * WEEKS_PER_MONTH, units),
      toPercent: (n) =>
        weightKg === null || weightKg <= 0
          ? 0
          : (displayToKg(n, units) / WEEKS_PER_MONTH / weightKg) * 100,
    },
  ]

  const missingSaid = (
    <p className="text-sm text-ink-muted">
      The expenditure needs {statsMissing ? 'your body stats' : ''}
      {statsMissing && weightMissing ? ' and ' : ''}
      {weightMissing ? 'a body weight' : ''} before it can be estimated.{' '}
      {statsMissing && (
        <DialogLink dialog="nutrition" className="text-accent underline">
          Fill in your stats
        </DialogLink>
      )}
      {statsMissing && weightMissing ? ' · ' : ''}
      {weightMissing && (
        <DialogLink dialog="weighin-weight" className="text-accent underline">
          Weigh in a weight
        </DialogLink>
      )}
    </p>
  )

  const card = 'space-y-4 rounded-2xl bg-surface-raised p-4 ring-1 ring-white/10'

  return (
    <form
      className="mx-auto max-w-3xl space-y-6"
      onSubmit={(event) => {
        event.preventDefault()
        if (step === 'review') {
          void save()
        } else {
          onward()
        }
      }}
    >
      <div className="flex items-center gap-2">
        {at === 0 ? (
          <Link
            to="/diary"
            search={withoutDialog}
            aria-label="Back to the diary"
            className="icon-btn -ml-2"
          >
            <ChevronLeftIcon aria-hidden className="size-7" />
          </Link>
        ) : (
          <button
            type="button"
            aria-label="Back a step"
            className="icon-btn -ml-2"
            onClick={backward}
          >
            <ChevronLeftIcon aria-hidden className="size-7" />
          </button>
        )}
        <h1 className="flex-1 text-3xl font-semibold">Goal</h1>
      </div>

      <p className="text-sm text-ink-muted">
        {`Step ${String(at + 1)} of ${String(steps.length)} · ${GOAL_STEP_TITLES[step]}`}
      </p>

      {goal !== null && at === 0 && (
        <p className="text-sm text-ink-muted">
          Set {goal.mode === 'guided' ? 'the coached way' : 'by hand'} from{' '}
          {formatDayLabel(dayStart(goal.effectiveFrom))}.
          {progress !== null &&
            ` ${distanceSaid(progress.distanceKg, units)}${
              progress.expectedDay === null
                ? ''
                : `, expected on ${formatDayLabel(dayStart(progress.expectedDay))}`
            }.`}
        </p>
      )}

      <section className={card}>
        <h2 className="text-lg font-medium">{GOAL_STEP_TITLES[step]}</h2>

        {step === 'way' && (
          <div role="radiogroup" aria-label="How the goal is set" className="flex gap-2">
            <Choice
              label="Coached"
              on={mode === 'guided'}
              onPick={() => {
                setMode('guided')
              }}
            />
            <Choice
              label="By hand"
              on={mode === 'manual'}
              onPick={() => {
                setMode('manual')
              }}
            />
          </div>
        )}

        {step === 'aim' && (
          <div role="radiogroup" aria-label="Aim" className="flex gap-2">
            {AIMS.map((each) => (
              <Choice
                key={each.aim}
                label={each.label}
                on={aim === each.aim}
                onPick={() => {
                  setAim(each.aim)
                }}
              />
            ))}
          </div>
        )}

        {step === 'rate' && (
          <>
            {asked.state === 'missing' ? (
              missingSaid
            ) : (
              <p className="text-sm text-ink-muted">
                {coached === null ? (
                  <Skeleton className="inline-block h-4 w-56 align-middle" />
                ) : (
                  <>
                    {`A daily budget of ${formatCalories(coached.calories)} to start with${
                      expectedDay === null
                        ? ''
                        : `, reaching your target on ${formatDayLabel(dayStart(expectedDay))}`
                    }.`}
                  </>
                )}
              </p>
            )}
            <UnitInput
              value={targetWeight}
              unit={units}
              label="Target weight"
              onValue={setTargetWeight}
            />
            {wrongSide && (
              <p className="text-sm text-danger">
                {`A target weight to ${aim} to has to be ${
                  aim === 'lose' ? 'below' : 'above'
                } your latest weight.`}
              </p>
            )}
            <label className="block">
              <span className="mb-1 block text-sm text-ink-muted">Rate</span>
              <input
                type="range"
                aria-label="Rate"
                min={0.05}
                max={RATE_CEILING_SHARE * 100}
                // Any value, and held inside the ends: the four fields beside the slider can
                // put a rate on it that no whole step of it would have landed on.
                step="any"
                value={Math.min(RATE_CEILING_SHARE * 100, Math.max(0.05, ratePercent))}
                onChange={(event) => {
                  setRatePut(null)
                  setRatePercent(Number(event.target.value))
                }}
                className="w-full accent-accent"
              />
            </label>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {rateFields.map((field) => (
                <UnitInput
                  key={field.key}
                  value={ratePut?.field === field.key ? ratePut.text : said(field.value)}
                  unit=""
                  label={field.label}
                  step="any"
                  onValue={(next) => {
                    setRatePut({ field: field.key, text: next })
                    const read = Number(next)
                    if (next.trim() !== '' && Number.isFinite(read) && read >= 0) {
                      setRatePercent(field.toPercent(read))
                    }
                  }}
                  onSettled={() => {
                    setRatePut(null)
                  }}
                />
              ))}
            </div>
            {rateHigh && (
              <p className="text-sm text-warmup">
                That is more than one per cent of your body weight a week — fast enough to cost
                muscle.
              </p>
            )}
          </>
        )}

        {step === 'maintain' && (
          <>
            <UnitInput
              value={targetWeight}
              unit={units}
              label="Target trend weight"
              onValue={setTargetWeight}
            />
            {trendKg !== null && (
              <p className="text-sm text-ink-muted">
                {`Your trend weight is ${said(kgToDisplay(trendKg, units), 1)} ${units}. A maintaining goal aims within ${String(MAINTAIN_WINDOW_KG)} kg of it, either way.`}
              </p>
            )}
            {outsideWindow && (
              <p className="text-sm text-danger">
                {`That is further than ${String(MAINTAIN_WINDOW_KG)} kg from your trend weight.`}
              </p>
            )}
            <p className="text-sm text-ink-muted">
              Dynamic maintenance: your calories are worked out again at every weekly check-in, so
              they follow what you actually burn and hold you at the weight you asked for rather
              than at a number set once.
            </p>
            {asked.state === 'missing' ? (
              missingSaid
            ) : coached?.range == null ? (
              <Skeleton className="h-4 w-56" />
            ) : (
              <p className="text-sm">
                {`Expect between ${formatCalories(coached.range.low)} and ${formatCalories(
                  coached.range.high,
                )} a day.`}
              </p>
            )}
          </>
        )}

        {step === 'diet' && (
          <>
            <div role="radiogroup" aria-label="Diet" className="flex flex-wrap gap-2">
              {DIETS.map((diet) => (
                <Choice
                  key={diet}
                  label={DIET_LABELS[diet]}
                  on={program.diet === diet}
                  onPick={() => {
                    setProgram((held) => ({ ...held, diet }))
                  }}
                />
              ))}
            </div>
            <p className="text-sm text-ink-muted">
              The diet says what share of a day goes to fat. The protein is set on its own step, and
              whatever calories are left over are carbohydrates.
            </p>
          </>
        )}

        {step === 'floor' && (
          <>
            <div role="radiogroup" aria-label="Calorie floor" className="flex flex-wrap gap-2">
              {CALORIE_FLOOR_CHOICES.map((floor) => (
                <Choice
                  key={floor}
                  label={CALORIE_FLOOR_LABELS[floor]}
                  on={program.calorieFloor === floor}
                  onPick={() => {
                    setProgram((held) => ({ ...held, calorieFloor: floor }))
                  }}
                />
              ))}
            </div>
            <p className="text-sm text-ink-muted">
              The floor is the fewest calories a day you will be coached to:{' '}
              {formatCalories(FLOOR_CALORIES[program.calorieFloor])}.
            </p>
            {program.calorieFloor === 'low' && (
              <p className="text-sm text-warmup">
                Proceed with caution. The low floor is under what most bodies need, and is not
                somewhere to sit for long without advice.
              </p>
            )}
          </>
        )}

        {step === 'protein' && (
          <>
            <div role="radiogroup" aria-label="Protein intake" className="flex flex-wrap gap-2">
              {PROTEIN_INTAKES.map((intake) => (
                <Choice
                  key={intake}
                  label={PROTEIN_INTAKE_LABELS[intake]}
                  on={program.proteinIntake === intake}
                  onPick={() => {
                    setProgram((held) => ({ ...held, proteinIntake: intake }))
                  }}
                />
              ))}
            </div>
            {coached !== null && (
              <p className="text-sm text-ink-muted">
                That comes to {String(coached.protein)} g of protein a day.
              </p>
            )}
          </>
        )}

        {step === 'distribution' && (
          <>
            <p className="text-sm text-ink-muted">
              A high day is paid for by the days that are not, so the week comes to the same
              whatever you choose here.
            </p>
            <ul className="space-y-2">
              {weekOrder().map((day) => (
                <li key={day.weekday} className="flex items-center justify-between gap-3">
                  <span>{day.name}</span>
                  <div role="radiogroup" aria-label={`${day.name} calories`} className="flex gap-2">
                    {CALORIE_LEVELS.map((level) => (
                      <Choice
                        key={level}
                        label={CALORIE_LEVEL_LABELS[level]}
                        on={program.distribution[day.weekday - 1] === level}
                        onPick={() => {
                          setProgram((held) => ({
                            ...held,
                            distribution: held.distribution.map((each, index) =>
                              index === day.weekday - 1 ? level : each,
                            ),
                          }))
                        }}
                      />
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}

        {step === 'spread' && (
          <div role="radiogroup" aria-label="How the week is set" className="flex flex-wrap gap-2">
            <Choice
              label="Same targets all week"
              on={sameAllWeek}
              onPick={() => {
                setSameAllWeek(true)
              }}
            />
            <Choice
              label="Different targets per day"
              on={!sameAllWeek}
              onPick={() => {
                setSameAllWeek(false)
              }}
            />
          </div>
        )}

        {step === 'manual-targets' &&
          (sameAllWeek ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {TARGETED.map((name) => (
                <UnitInput
                  key={name}
                  value={byHand[name]}
                  unit={NUTRIENT_UNITS[name]}
                  label={NUTRIENT_LABELS[name]}
                  onValue={(next) => {
                    setByHand((held) => ({ ...held, [name]: next }))
                  }}
                />
              ))}
            </div>
          ) : (
            <ul className="space-y-4">
              {weekOrder().map((day) => (
                <li key={day.weekday} className="space-y-2">
                  <h3 className="text-sm text-ink-muted">{day.name}</h3>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {TARGETED.map((name) => (
                      <UnitInput
                        key={name}
                        value={byHandDays[day.weekday - 1]?.[name] ?? ''}
                        unit={NUTRIENT_UNITS[name]}
                        label={`${day.name} ${NUTRIENT_LABELS[name]}`}
                        onValue={(next) => {
                          setByHandDays((held) =>
                            held.map((each, index) =>
                              index === day.weekday - 1 ? { ...each, [name]: next } : each,
                            ),
                          )
                        }}
                      />
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          ))}

        {step === 'review' && (
          <ReviewStep
            steps={steps}
            mode={mode}
            aim={aim}
            program={program}
            week={week}
            dayTargets={dayTargets}
            targetWeight={targetWeight === '' ? null : `${targetWeight} ${units}`}
            rateSaid={
              rateKg === null
                ? null
                : `${said(kgToDisplay(rateKg, units))} ${units} a week (${said(ratePercent)}% of body weight)`
            }
            expenditure={expenditure}
            range={coached?.range ?? null}
            expectedDay={mode === 'guided' && aim !== 'maintain' ? expectedDay : null}
            onChange={change}
          />
        )}
      </section>

      {stop?.said === true && <p className="text-sm text-ink-muted">{stop.why}</p>}

      <div className="flex items-center gap-3">
        {goal !== null && at === 0 && (
          <button
            type="button"
            className="flex items-center gap-1.5 rounded-full px-3 py-2 text-sm text-danger transition-colors hover:bg-danger/10"
            onClick={() => {
              setRemoving(true)
            }}
          >
            <DeleteIcon aria-hidden className="size-5" />
            Remove goal
          </button>
        )}
        <span className="flex-1" />
        <button
          type="submit"
          aria-busy={saving.working || undefined}
          disabled={blocker !== null && !saving.working}
          className="relative rounded-full bg-accent px-5 py-2 text-sm font-medium text-accent-ink disabled:opacity-50"
        >
          <Working shown={saving.shown}>{step === 'review' ? 'Set goal' : 'Next'}</Working>
        </button>
      </div>

      {leaving.status === 'blocked' && (
        <ConfirmDialog
          title="Discard your goal changes?"
          body="What you have answered here is not saved."
          confirm="Discard"
          onConfirm={() => {
            holding.current = false
            leaving.proceed()
          }}
          onDismiss={leaving.reset}
        />
      )}
      {removing && (
        <ConfirmDialog
          title="Remove the goal?"
          body="Every day loses the target that stood on it, today and the days already logged alike, and each names its total and nothing else."
          confirm="Remove"
          onConfirm={remove}
          onDismiss={() => {
            setRemoving(false)
          }}
        />
      )}
    </form>
  )
}

/**
 * The last step both ways end on: every answer that was given, and what the plan comes to. An
 * answer is changed from here, and answering it again comes straight back.
 */
function ReviewStep({
  steps,
  mode,
  aim,
  program,
  week,
  dayTargets,
  targetWeight,
  rateSaid,
  expenditure,
  range,
  expectedDay,
  onChange,
}: {
  steps: readonly GoalStep[]
  mode: GoalMode
  aim: Aim
  program: Program
  week: DayTargets | null
  dayTargets: readonly DayTargets[] | null
  targetWeight: string | null
  rateSaid: string | null
  expenditure: number | null
  range: { low: number; high: number } | null
  expectedDay: string | null
  onChange: (step: GoalStep) => void
}) {
  const answers: { step: GoalStep; label: string; said: string }[] = (
    [
      { step: 'way', label: 'Set', said: mode === 'guided' ? 'Coached' : 'By hand' },
      ...(mode === 'guided'
        ? [
            {
              step: 'aim' as const,
              label: 'Aim',
              said: aim === 'lose' ? 'Lose' : aim === 'gain' ? 'Gain' : 'Maintain',
            },
            {
              step: (aim === 'maintain' ? 'maintain' : 'rate') as GoalStep,
              label: aim === 'maintain' ? 'Target trend weight' : 'Target weight',
              said: targetWeight ?? 'Not said',
            },
            ...(aim === 'maintain'
              ? []
              : [{ step: 'rate' as const, label: 'Rate', said: rateSaid ?? 'Not said' }]),
            { step: 'diet' as const, label: 'Diet', said: DIET_LABELS[program.diet] },
            {
              step: 'floor' as const,
              label: 'Calorie floor',
              said: CALORIE_FLOOR_LABELS[program.calorieFloor],
            },
            {
              step: 'protein' as const,
              label: 'Protein intake',
              said: PROTEIN_INTAKE_LABELS[program.proteinIntake],
            },
            {
              step: 'distribution' as const,
              label: 'Calorie distribution',
              said: program.distribution.every((level) => level === 'normal')
                ? 'Level all week'
                : weekOrder()
                    .map(
                      (day) =>
                        `${day.name} ${CALORIE_LEVEL_LABELS[program.distribution[day.weekday - 1] ?? 'normal'].toLowerCase()}`,
                    )
                    .join(', '),
            },
          ]
        : [
            {
              step: 'spread' as const,
              label: 'The week',
              said: dayTargets === null ? 'Same targets all week' : 'Different targets per day',
            },
          ]),
    ] as { step: GoalStep; label: string; said: string }[]
  ).filter((answer) => steps.includes(answer.step))

  return (
    <div className="space-y-4">
      <dl className="space-y-1">
        {answers.map((answer) => (
          <div key={`${answer.step}-${answer.label}`} className="flex items-baseline gap-3">
            <dt className="w-40 shrink-0 text-sm text-ink-muted">{answer.label}</dt>
            <dd className="flex-1 text-sm">{answer.said}</dd>
            <button
              type="button"
              aria-label={`Change ${answer.label.toLowerCase()}`}
              className="text-sm text-accent underline"
              onClick={() => {
                onChange(answer.step)
              }}
            >
              Change
            </button>
          </div>
        ))}
      </dl>

      <div className="space-y-2 rounded-2xl bg-surface p-3">
        <h3 className="text-sm text-ink-muted">Your plan</h3>
        {week === null ? (
          <Skeleton className="h-5 w-56" />
        ) : (
          <>
            <p className="text-sm">
              {`${formatCalories(week.calories)} a day · ${String(week.protein)} g protein · ${String(
                week.carbs,
              )} g carbs · ${String(week.fat)} g fat`}
            </p>
            {expenditure !== null && (
              <p className="text-sm text-ink-muted">
                Against an expenditure of {formatCalories(expenditure)} a day.
              </p>
            )}
            {range !== null && (
              <p className="text-sm text-ink-muted">
                Maintaining moves between {formatCalories(range.low)} and{' '}
                {formatCalories(range.high)} a day as the trend weight moves.
              </p>
            )}
            {expectedDay !== null && (
              <p className="text-sm text-ink-muted">
                Your target is expected on {formatDayLabel(dayStart(expectedDay))}.
              </p>
            )}
            {dayTargets !== null && !isEvenWeek(dayTargets) && (
              <ul className="space-y-1 pt-1">
                {weekOrder().map((day) => {
                  const targets = dayTargets[day.weekday - 1]
                  return targets === undefined ? null : (
                    <li key={day.weekday} className="flex gap-3 text-sm">
                      <span className="w-24 shrink-0 text-ink-muted">{day.name}</span>
                      <span>
                        {formatCalories(targets.calories)} · {String(targets.protein)} g ·{' '}
                        {String(targets.carbs)} g · {String(targets.fat)} g
                      </span>
                    </li>
                  )
                })}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  )
}
